import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Edit, Trash2, Search, Phone, MapPin, Upload, X, User, AlertCircle } from 'lucide-react';
import { Coach } from '../types';
import FloatingActionButton from '../components/FloatingActionButton';
import { getCoaches, createCoach, updateCoach, deleteCoach } from '../services/coachService';

const Coaches: React.FC = () => {
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCoach, setCurrentCoach] = useState<Coach | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [formData, setFormData] = useState<Omit<Coach, 'id'>>({
    name: '',
    address: '',
    phone: '',
    profileImage: '',
    squads: []
  });

  // Load coaches on component mount
  useEffect(() => {
    loadCoaches();
  }, []);

  const loadCoaches = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getCoaches();
      setCoaches(data);
    } catch (err) {
      console.error('Error loading coaches:', err);
      setError('Failed to load coaches. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSquadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, squads: [...prev.squads, value as 'U11' | 'U13' | 'U15' | 'U17'] };
      } else {
        return { ...prev, squads: prev.squads.filter(squad => squad !== value) };
      }
    });
  };

  const handleAddCoach = () => {
    setCurrentCoach(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      profileImage: '',
      squads: []
    });
    setIsModalOpen(true);
  };

  const handleEditCoach = (coach: Coach) => {
    setCurrentCoach(coach);
    setFormData({
      name: coach.name,
      address: coach.address,
      phone: coach.phone || '',
      profileImage: coach.profileImage || '',
      squads: [...coach.squads]
    });
    setIsModalOpen(true);
  };

  const handleDeleteCoach = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this coach?')) {
      try {
        await deleteCoach(id);
        setCoaches(coaches.filter(coach => coach.id !== id));
      } catch (err) {
        console.error('Error deleting coach:', err);
        alert('Failed to delete coach. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (currentCoach) {
        // Update existing coach
        const updatedCoach = await updateCoach(currentCoach.id, formData);
        setCoaches(coaches.map(coach => 
          coach.id === currentCoach.id ? updatedCoach : coach
        ));
      } else {
        // Add new coach
        const newCoach = await createCoach(formData);
        setCoaches([...coaches, newCoach]);
      }
      
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving coach:', err);
      alert('Failed to save coach. Please try again.');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // In a real app, we would upload the file to a storage service here
    // For this demo, we'll create a fake URL using a placeholder image from Unsplash
    const imageUrl = `https://images.unsplash.com/photo-${Date.now()}?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60`;
    
    setFormData(prev => ({
      ...prev,
      profileImage: imageUrl
    }));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      profileImage: ''
    }));
  };

  const filteredCoaches = coaches.filter(coach => 
    coach.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coach.squads.some(squad => squad.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (coach.phone && coach.phone.includes(searchTerm))
  );

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading coaches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={loadCoaches}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Coaches</h1>
        <button
          onClick={handleAddCoach}
          className="btn-primary flex items-center md:flex hidden"
        >
          <UserPlus size={20} className="mr-2" />
          <span className="hidden sm:inline">Add Coach</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search coaches by name, squad, or phone..."
            className="pl-10 form-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredCoaches.length > 0 ? (
            filteredCoaches.map((coach) => (
              <li key={coach.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {coach.profileImage ? (
                      <img 
                        src={coach.profileImage} 
                        alt={coach.name} 
                        className="h-12 w-12 rounded-full object-cover mr-4"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-navy-100 flex items-center justify-center mr-4">
                        <User size={24} className="text-navy-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{coach.name}</h3>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Phone size={16} className="flex-shrink-0 mr-1.5 text-gray-400" />
                        <span>{coach.phone || 'No phone number'}</span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <MapPin size={16} className="flex-shrink-0 mr-1.5 text-gray-400" />
                        <span>{coach.address}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {coach.squads.map((squad) => (
                        <span key={squad} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {squad}
                        </span>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditCoach(coach)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        aria-label="Edit coach"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteCoach(coach.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        aria-label="Delete coach"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="px-4 py-6 text-center text-gray-500">
              No coaches found. Try adjusting your search or add a new coach.
            </li>
          )}
        </ul>
      </div>

      {/* Mobile view for coaches */}
      <div className="md:hidden mt-6 space-y-4">
        {filteredCoaches.map((coach) => (
          <div key={coach.id} className="bg-white rounded-lg shadow-md p-4">
            <div className="flex justify-between">
              <div className="flex items-center">
                {coach.profileImage ? (
                  <img 
                    src={coach.profileImage} 
                    alt={coach.name} 
                    className="h-12 w-12 rounded-full object-cover mr-3"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-navy-100 flex items-center justify-center mr-3">
                    <User size={24} className="text-navy-600" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{coach.name}</h3>
                  <p className="text-sm text-gray-500">{coach.phone}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditCoach(coach)}
                  className="text-blue-600 hover:text-blue-900 p-1"
                  aria-label="Edit coach"
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => handleDeleteCoach(coach.id)}
                  className="text-red-600 hover:text-red-900 p-1"
                  aria-label="Delete coach"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="mt-3 flex flex-wrap gap-1">
              {coach.squads.map((squad) => (
                <span key={squad} className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  {squad}
                </span>
              ))}
            </div>
            
            <div className="mt-2 text-sm text-gray-500 flex items-center">
              <MapPin size={16} className="mr-1.5 text-gray-400" />
              <span>{coach.address}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Floating Action Button for mobile */}
      <FloatingActionButton to="#" onClick={handleAddCoach} />

      {/* Add/Edit Coach Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-auto modal-content mobile-scroll overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                {currentCoach ? 'Edit Coach' : 'Add New Coach'}
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-4">
                  {/* Profile Image Upload */}
                  <div className="form-group">
                    <label className="form-label">Profile Image</label>
                    <div className="mt-2 flex justify-center">
                      <div className="relative">
                        {formData.profileImage ? (
                          <div className="relative">
                            <img 
                              src={formData.profileImage} 
                              alt="Coach profile" 
                              className="h-32 w-32 rounded-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={removeImage}
                              className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                              aria-label="Remove image"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div 
                            onClick={triggerFileInput}
                            className="h-32 w-32 rounded-full bg-gray-100 flex flex-col items-center justify-center cursor-pointer border-2 border-dashed border-gray-300 hover:border-navy-500"
                          >
                            <Upload size={24} className="text-gray-400 mb-2" />
                            <span className="text-xs text-gray-500">Upload Image</span>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 text-center">
                      Click to upload a profile image (optional)
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Coach Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+44 7700 900000"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address" className="form-label">
                      Address
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      rows={2}
                      className="form-input"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Squads</label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center">
                        <input
                          id="squad-u11"
                          name="squad-u11"
                          type="checkbox"
                          value="U11"
                          checked={formData.squads.includes('U11')}
                          onChange={handleSquadChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="squad-u11" className="ml-2 block text-sm text-gray-900">
                          U11
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="squad-u13"
                          name="squad-u13"
                          type="checkbox"
                          value="U13"
                          checked={formData.squads.includes('U13')}
                          onChange={handleSquadChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="squad-u13" className="ml-2 block text-sm text-gray-900">
                          U13
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="squad-u15"
                          name="squad-u15"
                          type="checkbox"
                          value="U15"
                          checked={formData.squads.includes('U15')}
                          onChange={handleSquadChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="squad-u15" className="ml-2 block text-sm text-gray-900">
                          U15
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="squad-u17"
                          name="squad-u17"
                          type="checkbox"
                          value="U17"
                          checked={formData.squads.includes('U17')}
                          onChange={handleSquadChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="squad-u17" className="ml-2 block text-sm text-gray-900">
                          U17
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
                <button
                  type="button"
                  className="btn-outline mr-2"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {currentCoach ? 'Update Coach' : 'Add Coach'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coaches;